// EventForm.jsx
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { createEvent, updateEvent, deleteEvent, fetchEventById, fetchEvents, fetchActiveSellers } from '../../Redux/Actions/actions';
import styles from './EventForm.module.css';

const TIPO_OPTIONS = [
  { value: 'reunion', label: 'Reunión' },
  { value: 'visita', label: 'Visita' },
  { value: 'recordatorio', label: 'Recordatorio' },
  { value: 'vencimiento', label: 'Vencimiento' },
  { value: 'personal', label: 'Personal' },
  { value: 'otro', label: 'Otro' },
];

const PRIORIDAD_OPTIONS = [
  { value: 'baja', label: '🟢 Baja' },
  { value: 'media', label: '🟡 Media' },
  { value: 'alta', label: '🟠 Alta' },
  { value: 'urgente', label: '🔴 Urgente' },
];

const COLOR_PRESETS = [
  '#2196F3', '#4CAF50', '#FF9800', '#F44336', '#9C27B0', '#607D8B',
  '#00BCD4', '#E91E63', '#3F51B5', '#795548',
];

const TIPO_COLORS = {
  reunion: '#2196F3',
  visita: '#4CAF50',
  recordatorio: '#FF9800',
  vencimiento: '#F44336',
  personal: '#9C27B0',
  otro: '#607D8B',
};

const normalizeUserType = (value) => {
  const v = String(value || '').trim().toUpperCase();
  if (v === 'MARTILLER') return 'MARTILLER';
  if (v === 'SELLER' || v === 'VENDEDOR') return 'SELLER';
  return 'SELLER';
};

const EventForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(state => state.user);
  const userType = useSelector(state => state.userType);
  const sellersRaw = useSelector(state => state.activeSellers);
  const sellers = Array.isArray(sellersRaw) ? sellersRaw : (Array.isArray(sellersRaw?.data) ? sellersRaw.data : []);
  const currentEvent = useSelector(state => state.currentEvent);
  const eventosRaw = useSelector(state => state.events);
  const eventos = Array.isArray(eventosRaw) ? eventosRaw : [];
  const loading = useSelector(state => state.eventsLoading);

  const [form, setForm] = useState({
    titulo: '',
    descripcion: '',
    fecha: '',
    horaInicio: '',
    horaFin: '',
    tipo: 'otro',
    prioridad: 'media',
    color: '',
    ubicacion: '',
    todoElDia: false,
    notas: '',
    agentes: [],
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const currentUserId = user?.user?.id || user?.id || null;
  const currentUserType = normalizeUserType(user?.user?.type || user?.type || userType);

  useEffect(() => {
    dispatch(fetchActiveSellers());
    dispatch(fetchEvents());
  }, [dispatch]);

  useEffect(() => {
    if (isEdit) {
      dispatch(fetchEventById(id));
    }
  }, [dispatch, id, isEdit]);

  useEffect(() => {
    if (isEdit && currentEvent && currentEvent.id === Number(id)) {
      setForm({
        titulo: currentEvent.titulo || '',
        descripcion: currentEvent.descripcion || '',
        fecha: currentEvent.fecha || '',
        horaInicio: currentEvent.horaInicio ? currentEvent.horaInicio.slice(0, 5) : '',
        horaFin: currentEvent.horaFin ? currentEvent.horaFin.slice(0, 5) : '',
        tipo: currentEvent.tipo || 'otro',
        prioridad: currentEvent.prioridad || 'media',
        color: currentEvent.color || '',
        ubicacion: currentEvent.ubicacion || '',
        todoElDia: currentEvent.todoElDia || false,
        notas: currentEvent.notas || '',
        agentes: currentEvent.agentes ? currentEvent.agentes.map(a => a.id) : [],
      });
    }
  }, [currentEvent, id, isEdit]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const handleAgentToggle = (agentId) => {
    setForm(prev => ({
      ...prev,
      agentes: prev.agentes.includes(agentId)
        ? prev.agentes.filter(id => id !== agentId)
        : [...prev.agentes, agentId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const agentesPayload = (form.agentes || []).map((agentId) => ({
      id: Number(agentId),
      tipo: 'SELLER',
    }));

    const eventData = {
      ...form,
      creadorId: currentUserId,
      creadorTipo: currentUserType,
      agentes: agentesPayload,
    };

    try {
      if (isEdit) {
        await dispatch(updateEvent(id, eventData));
      } else {
        await dispatch(createEvent(eventData));
      }
      setSubmitted(true);
      dispatch(fetchEvents());
      setTimeout(() => {
        setSubmitted(false);
        if (isEdit) navigate('/eventos/crear');
        else {
          setForm({
            titulo: '', descripcion: '', fecha: '', horaInicio: '', horaFin: '',
            tipo: 'otro', prioridad: 'media', color: '', ubicacion: '',
            todoElDia: false, notas: '', agentes: [],
          });
        }
      }, 1500);
    } catch (err) {
      setError('Error al guardar el evento. Intentá de nuevo.');
    }
  };

  const handleDelete = async () => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este evento?')) {
      try {
        await dispatch(deleteEvent(id));
        navigate('/eventos/crear');
      } catch {
        setError('Error al eliminar el evento.');
      }
    }
  };

  const handleDeleteFromList = async (eventId) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este evento?')) {
      try {
        await dispatch(deleteEvent(eventId));
        dispatch(fetchEvents());
      } catch {
        setError('Error al eliminar el evento.');
      }
    }
  };

  const formatDisplayDate = (fecha) => {
    if (!fecha) return '';
    if (/^\d{4}-\d{2}-\d{2}/.test(fecha)) {
      const [y, m, d] = fecha.slice(0, 10).split('-');
      return `${d}/${m}/${y}`;
    }
    return fecha;
  };

  if (submitted) {
    return (
      <div className={styles.successContainer}>
        <div className={styles.successIcon}>✓</div>
        <h2>{isEdit ? 'Evento actualizado' : 'Evento creado'} con éxito</h2>
        <p>Redirigiendo al calendario...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.formHeader}>
          <h2 className={styles.title}>{isEdit ? 'Editar Evento' : 'Crear Evento'}</h2>
          <button type="button" className={styles.backBtn} onClick={() => navigate('/calendario')}>
            Calendario
          </button>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        {/* Fila principal */}
        <div className={styles.row}>
          <div className={styles.field}>
            <label className={styles.label}>Título *</label>
            <input name="titulo" value={form.titulo} onChange={handleChange} required className={styles.input} placeholder="Ej: Reunión de equipo" />
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.field}>
            <label className={styles.label}>Descripción</label>
            <textarea name="descripcion" value={form.descripcion} onChange={handleChange} className={styles.textarea} rows={3} placeholder="Detalles del evento..." />
          </div>
        </div>

        <div className={styles.row3}>
          <div className={styles.field}>
            <label className={styles.label}>Fecha *</label>
            <input type="date" name="fecha" value={form.fecha} onChange={handleChange} required className={styles.input} />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Hora Inicio</label>
            <input type="time" name="horaInicio" value={form.horaInicio} onChange={handleChange} className={styles.input} disabled={form.todoElDia} />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Hora Fin</label>
            <input type="time" name="horaFin" value={form.horaFin} onChange={handleChange} className={styles.input} disabled={form.todoElDia} />
          </div>
        </div>

        <div className={styles.checkboxRow}>
          <label className={styles.checkboxLabel}>
            <input type="checkbox" name="todoElDia" checked={form.todoElDia} onChange={handleChange} />
            <span>Todo el día</span>
          </label>
        </div>

        <div className={styles.row3}>
          <div className={styles.field}>
            <label className={styles.label}>Tipo *</label>
            <select name="tipo" value={form.tipo} onChange={handleChange} className={styles.select}>
              {TIPO_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Prioridad</label>
            <select name="prioridad" value={form.prioridad} onChange={handleChange} className={styles.select}>
              {PRIORIDAD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Ubicación</label>
            <input name="ubicacion" value={form.ubicacion} onChange={handleChange} className={styles.input} placeholder="Dirección o lugar" />
          </div>
        </div>

        {/* Color */}
        <div className={styles.field}>
          <label className={styles.label}>Color en calendario</label>
          <div className={styles.colorRow}>
            {COLOR_PRESETS.map(c => (
              <button
                key={c}
                type="button"
                className={`${styles.colorDot} ${form.color === c ? styles.colorDotActive : ''}`}
                style={{ background: c }}
                onClick={() => setForm({ ...form, color: c })}
              />
            ))}
            <input
              type="color"
              value={form.color || '#607D8B'}
              onChange={e => setForm({ ...form, color: e.target.value })}
              className={styles.colorInput}
              title="Color personalizado"
            />
            {form.color && (
              <button type="button" className={styles.clearColor} onClick={() => setForm({ ...form, color: '' })}>
                ✕ Quitar
              </button>
            )}
          </div>
        </div>

        {/* Notas */}
        <div className={styles.field}>
          <label className={styles.label}>Notas internas</label>
          <textarea name="notas" value={form.notas} onChange={handleChange} className={styles.textarea} rows={2} placeholder="Notas privadas..." />
        </div>

        {/* Selector de agentes */}
        <div className={styles.field}>
          <label className={styles.label}>Asignar agentes</label>
          <div className={styles.agentGrid}>
            {sellers.map(s => (
              <label key={s.id} className={`${styles.agentCard} ${form.agentes.includes(s.id) ? styles.agentCardActive : ''}`}>
                <input
                  type="checkbox"
                  checked={form.agentes.includes(s.id)}
                  onChange={() => handleAgentToggle(s.id)}
                  className={styles.agentCheckbox}
                />
                {s.photo && <img src={s.photo} alt="" className={styles.agentPhoto} />}
                <span className={styles.agentName}>{s.name} {s.last_name}</span>
              </label>
            ))}
          </div>
          {form.agentes.length > 0 && (
            <span className={styles.agentCount}>{form.agentes.length} agente(s) seleccionado(s)</span>
          )}
        </div>

        {/* Acciones */}
        <div className={styles.actions}>
          <button className={styles.submitBtn} type="submit" disabled={loading}>
            {loading ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Crear evento'}
          </button>
          {isEdit && (
            <button type="button" className={styles.deleteBtn} onClick={handleDelete} disabled={loading}>
              🗑️ Eliminar
            </button>
          )}
        </div>
      </form>

      {/* Sección de gestión de eventos */}
      <div className={styles.managementSection}>
        <h3 className={styles.managementTitle}>Gestión de Eventos</h3>
        {eventos.length === 0 && !loading && (
          <p className={styles.noEvents}>No hay eventos creados aún.</p>
        )}
        {loading && <p className={styles.loadingText}>Cargando eventos...</p>}
        <div className={styles.eventList}>
          {eventos.map(ev => (
            <div key={ev.id} className={styles.eventRow}>
              <div className={styles.eventColor} style={{ background: ev.color || TIPO_COLORS[ev.tipo] || '#607D8B' }} />
              <div className={styles.eventInfo}>
                <span className={styles.eventName}>{ev.titulo}</span>
                <span className={styles.eventMeta}>
                  {formatDisplayDate(ev.fecha)}
                  {ev.horaInicio ? ` · ${ev.horaInicio}` : ''}
                  {ev.tipo ? ` · ${TIPO_OPTIONS.find(t => t.value === ev.tipo)?.label || ev.tipo}` : ''}
                </span>
              </div>
              <div className={styles.eventStatus} data-estado={ev.estado || 'pendiente'}>
                {ev.estado === 'confirmado' ? 'Confirmado' :
                 ev.estado === 'completado' ? 'Completado' :
                 ev.estado === 'cancelado' ? 'Cancelado' : 'Pendiente'}
              </div>
              <div className={styles.eventActions}>
                <button
                  className={styles.editListBtn}
                  onClick={() => navigate(`/eventos/editar/${ev.id}`)}
                  title="Editar"
                >
                  ✏️
                </button>
                <button
                  className={styles.deleteListBtn}
                  onClick={() => handleDeleteFromList(ev.id)}
                  title="Eliminar"
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EventForm;